import { Box, Divider, Stack, Typography } from "@mui/material";
import React from "react";
import assignmentsBorder from "../../../../../../assets/assignments-border.jpg";
import assignmentsShadows from "../../../../../../assets/assignments-shadows.jpg";
import completedChart from "../../../../../../assets/completed-chart.jpg";
import overdueChart from "../../../../../../assets/overdue-chart.jpg";
import pendingChart from "../../../../../../assets/pending-chart.jpg";
import ColorPicker from "../../../../../../components/ColorPicker/ColorPicker";
import {
  AttributeKey,
  PageUid,
  useCustomisation,
} from "../../../../../../context/CustomisationContext";
import { usePortal } from "../../../../../../context/PortalsContext";
import { updateSectionDesign } from "../../../../../../services/api/apiClient";
import { brand, gray } from "../../../../../../theme/themePrimitives";

interface AssignmentsCustomisationProps {
  pageId?: PageUid;
}

const borderSizes = ["N/A", "2px", "4px", "8px"];
const shadowSizes = ["N/A", "XS", "SM", "MD", "LG"];

export const AssignmentsCustomisation: React.FC<
  AssignmentsCustomisationProps
> = ({ pageId = "GPC" }) => {
  const { updateCustomisationSection, getCustomisationValue } =
    useCustomisation();
  const { portals } = usePortal();

  const completedKey = `${pageId.toLowerCase()}Completed`;
  const pendingKey = `${pageId.toLowerCase()}Pending`;
  const overdueKey = `${pageId.toLowerCase()}Overdue`;
  const borderColorKey = `${pageId.toLowerCase()}BorderColor`;
  const borderSizeKey = `${pageId.toLowerCase()}BorderSize`;
  const shadowColorKey = `${pageId.toLowerCase()}ShadowColor`;
  const shadowSizeKey = `${pageId.toLowerCase()}ShadowSize`;

  const handleAttributeChange = (
    attributeKey: string,
    newAttributeValue?: string,
  ) => {
    if (newAttributeValue === undefined) return;

    updateCustomisationSection(pageId, "assignmentStatusColorsSettings", {
      [attributeKey]: newAttributeValue,
    });

    updateSectionDesign(
      portals.selectedPortal?.portalUid!,
      pageId,
      "assignmentStatusColorsSettings",
      {
        attributeKey,
        attributeValue: newAttributeValue,
      },
    );
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" fontWeight="bold">
          Assignment status colors
        </Typography>
      </Box>
      <Divider />
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          Pie chart
        </Typography>
        <Stack
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            px: 2,
          }}
          spacing={1}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              py: 2,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography>
                Completed chart Color
              </Typography>
              <ColorPicker
                value={getCustomisationValue(
                  pageId,
                  "assignmentStatusColorsSettings",
                  completedKey as AttributeKey,
                )}
                onChange={(color) => handleAttributeChange(completedKey, color)}
              />
            </Box>
            <Box
              sx={{ width: "100%", height: "200px" }}
              component={"img"}
              src={completedChart}
              alt="completed-chart"
            />
          </Box>
          <Divider />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              py: 2,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography>
                Pending chart Color
              </Typography>
              <ColorPicker
                value={getCustomisationValue(
                  pageId,
                  "assignmentStatusColorsSettings",
                  pendingKey as AttributeKey,
                )}
                onChange={(color) => handleAttributeChange(pendingKey, color)}
              />
            </Box>
            <Box
              sx={{ width: "100%", height: "200px" }}
              component={"img"}
              src={pendingChart}
              alt="pending-chart"
            />
          </Box>
          <Divider />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              py: 2,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography>
                Overdue chart Color
              </Typography>
              <ColorPicker
                value={getCustomisationValue(
                  pageId,
                  "assignmentStatusColorsSettings",
                  overdueKey as AttributeKey,
                )}
                onChange={(color) => handleAttributeChange(overdueKey, color)}
              />
            </Box>
            <Box
              sx={{ width: "100%", height: "200px" }}
              component={"img"}
              src={overdueChart}
              alt="overdue-chart"
            />
          </Box>
        </Stack>
      </Stack>
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          Borders
        </Typography>
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            px: 2,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              py: 2,
              gap: 2,
            }}
          >
            <Stack spacing={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography>Border Color</Typography>
                <ColorPicker
                  value={getCustomisationValue(
                    pageId,
                    "assignmentStatusColorsSettings",
                    borderColorKey as AttributeKey,
                  )}
                  onChange={(color) =>
                    handleAttributeChange(borderColorKey, color)
                  }
                />
              </Box>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography>Border size</Typography>
                  <Typography
                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                    color={brand[600]}
                    onClick={() => {
                      handleAttributeChange(borderColorKey, "");
                      handleAttributeChange(borderSizeKey, "");
                    }}
                  >
                    Clear
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    gap: 2,
                  }}
                >
                  {borderSizes.map((value) => {
                    const isSelected =
                      value === "N/A"
                        ? getCustomisationValue(
                            pageId,
                            "assignmentStatusColorsSettings",
                            borderSizeKey as AttributeKey,
                          ) === "0px"
                        : getCustomisationValue(
                            pageId,
                            "assignmentStatusColorsSettings",
                            borderSizeKey as AttributeKey,
                          ) === value;

                    return (
                      <Box
                        key={value}
                        onClick={() =>
                          handleAttributeChange(
                            borderSizeKey,
                            value === "N/A" ? "0px" : value,
                          )
                        }
                        sx={{
                          cursor: "pointer",
                          border: `${value === "N/A" ? "1px" : value} solid ${
                            isSelected ? brand[600] : gray[300]
                          }`,
                          boxSizing: "border-box",
                          height: "50px",
                          borderRadius: "12px",
                          padding: "8px 16px",
                          textAlign: "center",
                          color: isSelected ? brand[600] : "inherit",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "border 0.3s ease, color 0.3s ease",
                        }}
                      >
                        <Typography variant="body2">{value}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Stack>
            <Box
              sx={{ width: "100%", height: "200px" }}
              component={"img"}
              src={assignmentsBorder}
              alt="assignments-border"
            />
          </Box>
        </Box>
      </Stack>
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          Shadows
        </Typography>
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            px: 2,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              py: 2,
              gap: 2,
            }}
          >
            <Stack spacing={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography>Shadow Color</Typography>
                <ColorPicker
                  value={getCustomisationValue(
                    pageId,
                    "assignmentStatusColorsSettings",
                    shadowColorKey as AttributeKey,
                  )}
                  onChange={(color) =>
                    handleAttributeChange(shadowColorKey, color)
                  }
                />
              </Box>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography>Shadow size</Typography>
                  <Typography
                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                    color={brand[600]}
                    onClick={() => {
                      handleAttributeChange(shadowColorKey, "");
                      handleAttributeChange(shadowSizeKey, "");
                    }}
                  >
                    Clear
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
                    gap: 2,
                  }}
                >
                  {shadowSizes.map((s) => {
                    const isSelected =
                      getCustomisationValue(
                        pageId,
                        "assignmentStatusColorsSettings",
                        shadowSizeKey as AttributeKey,
                      ) === s;

                    return (
                      <Box
                        key={s}
                        onClick={() => handleAttributeChange(shadowSizeKey, s)}
                        sx={{
                          cursor: "pointer",
                          border: `1px solid ${
                            isSelected ? brand[600] : gray[300]
                          }`,
                          height: "50px",
                          borderRadius: "12px",
                          padding: "8px 16px",
                          textAlign: "center",
                          color: isSelected ? brand[600] : "inherit",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "border 0.3s ease, color 0.3s ease",
                        }}
                      >
                        <Typography variant="body2">{s}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Stack>
            <Box
              sx={{ width: "100%", height: "200px" }}
              component={"img"}
              src={assignmentsShadows}
              alt="assignments-shadows"
            />
          </Box>
        </Box>
      </Stack>
    </Stack>
  );
};
